/**
 * UpgradePrompt Component
 *
 * @description プランアップグレード誘導コンポーネント - Issue #286対応
 * @features プラン比較、料金表示、Stripe Checkout統合
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  CircularProgress,
  Grid,
  IconButton,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  TrendingUp as UpgradeIcon,
  Star as StarIcon,
  CreditCard as PaymentIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import stripeService from '../../services/stripeService';
import type { PlanInfo } from '../../types/billing';
import { useStore } from '../../store/useStore';

/**
 * プラン順序定義（比較用）
 */
const PLAN_ORDER = {
  light: 0,
  standard: 1,
  premium: 2,
} as const;

/**
 * プラン変更のアクションタイプを判定
 */
const getActionType = (currentPlanId: string, targetPlanId: string): 'upgrade' | 'downgrade' | 'change' => {
  const currentLevel = PLAN_ORDER[currentPlanId as keyof typeof PLAN_ORDER] ?? 0;
  const targetLevel = PLAN_ORDER[targetPlanId as keyof typeof PLAN_ORDER] ?? 0;

  if (targetLevel > currentLevel) return 'upgrade';
  if (targetLevel < currentLevel) return 'downgrade';
  return 'change';
};

/**
 * プラン変更のアクションテキストを取得
 */
const getActionText = (actionType: 'upgrade' | 'downgrade' | 'change'): string => {
  switch (actionType) {
    case 'upgrade':
      return 'アップグレード';
    case 'downgrade':
      return 'ダウングレード';
    case 'change':
      return '変更';
    default:
      return '変更';
  }
};

/**
 * プラン定義（3プラン体系）
 */
const PLANS: Record<string, PlanInfo> = {
  light: {
    id: 'light' as const,
    name: 'ライトプラン',
    description: '小規模サロン向けの基本プラン',
    monthlyFee: 19800,
    yearlyFee: 213840, // 10%割引
    billingCycle: 'monthly',
    features: [
      { id: 'staff_5', name: 'スタッフ登録', description: '最大5名まで', category: 'core', included: true },
      { id: 'reservation_500', name: '月間予約', description: '500件まで', category: 'core', included: true },
      { id: 'customer_1000', name: '顧客管理', description: '1,000名まで', category: 'core', included: true },
      { id: 'storage_10', name: 'データ容量', description: '10GB', category: 'core', included: true },
      { id: 'basic_report', name: '基本レポート', description: '売上・予約分析', category: 'core', included: true },
      { id: 'email_support', name: 'サポート', description: 'メールサポート', category: 'core', included: true },
    ],
    limits: {
      staffCount: 5,
      reservationsPerMonth: 500,
      customersLimit: 1000,
      storageGB: 10,
      aiAnalysisCount: 0,
      supportLevel: 'email',
    },
    isActive: true,
  },
  standard: {
    id: 'standard' as const,
    name: 'スタンダードプラン',
    description: '成長中のサロン向け、AI分析機能付き',
    monthlyFee: 39800,
    yearlyFee: 429840, // 10%割引
    billingCycle: 'monthly',
    features: [
      { id: 'staff_20', name: 'スタッフ登録', description: '最大20名まで', category: 'core', included: true },
      { id: 'reservation_2000', name: '月間予約', description: '2,000件まで', category: 'core', included: true },
      { id: 'customer_5000', name: '顧客管理', description: '5,000名まで', category: 'core', included: true },
      { id: 'storage_50', name: 'データ容量', description: '50GB', category: 'core', included: true },
      { id: 'ai_analysis', name: 'AI分析', description: '予測・最適化', category: 'advanced', included: true },
      { id: 'external_api', name: '外部連携', description: 'ホットペッパー等', category: 'advanced', included: true },
      { id: 'advanced_report', name: '詳細レポート', description: 'カスタムレポート', category: 'advanced', included: true },
      { id: 'chat_support', name: 'サポート', description: 'チャットサポート', category: 'advanced', included: true },
    ],
    limits: {
      staffCount: 20,
      reservationsPerMonth: 2000,
      customersLimit: 5000,
      storageGB: 50,
      aiAnalysisCount: 100,
      supportLevel: 'chat',
    },
    isActive: true,
  },
  premium: {
    id: 'premium' as const,
    name: 'プレミアムプラン',
    description: '大規模サロン・チェーン向け完全機能',
    monthlyFee: 79800,
    yearlyFee: 861840, // 10%割引
    billingCycle: 'monthly',
    features: [
      { id: 'staff_unlimited', name: 'スタッフ登録', description: '無制限', category: 'premium', included: true },
      { id: 'reservation_unlimited', name: '月間予約', description: '無制限', category: 'premium', included: true },
      { id: 'customer_unlimited', name: '顧客管理', description: '無制限', category: 'premium', included: true },
      { id: 'storage_unlimited', name: 'データ容量', description: '無制限', category: 'premium', included: true },
      { id: 'ai_unlimited', name: 'AI分析', description: '無制限', category: 'premium', included: true },
      { id: 'api_full', name: 'API完全アクセス', description: 'カスタム統合可能', category: 'premium', included: true },
      { id: 'white_label', name: 'ホワイトラベル', description: '独自ブランディング', category: 'premium', included: true },
      { id: 'custom_branding', name: 'カスタムブランド', description: '独自ブランディング', category: 'premium', included: true },
      { id: 'phone_support', name: '専任サポート', description: '電話・優先対応', category: 'premium', included: true },
    ],
    limits: {
      staffCount: 999999, // 実質無制限
      reservationsPerMonth: 999999,
      customersLimit: 999999,
      storageGB: 999999,
      aiAnalysisCount: 999999,
      supportLevel: 'phone',
    },
    isActive: true,
  },
};

/**
 * UpgradePrompt Props
 */
interface UpgradePromptProps {
  /** 現在のプラン */
  currentPlan?: PlanInfo;
  /** 推奨プラン */
  recommendedPlan?: 'light' | 'standard' | 'premium';
  /** 機能名（特定機能からのアップグレード） */
  feature?: string;
  /** ダイアログを開く */
  open: boolean;
  /** ダイアログを閉じる */
  onClose: () => void;
  /** カスタムタイトル */
  title?: string;
  /** カスタムメッセージ */
  message?: string;
}

/**
 * プラン比較テーブル
 */
const PlanComparisonTable: React.FC<{
  currentPlanId?: string;
  targetPlanId?: string;
  onSelectPlan: (planId: string) => void;
}> = ({ currentPlanId = 'light', targetPlanId, onSelectPlan }) => {
  const features = [
    { key: 'スタッフ登録', light: '5名', standard: '20名', premium: '無制限' },
    { key: '月間予約', light: '500件', standard: '2,000件', premium: '無制限' },
    { key: '顧客管理', light: '1,000名', standard: '5,000名', premium: '無制限' },
    { key: 'データ容量', light: '10GB', standard: '50GB', premium: '無制限' },
    { key: 'AI分析', light: '×', standard: '◯', premium: '◯' },
    { key: '外部API連携', light: '×', standard: '◯', premium: '◯' },
    { key: 'ホワイトラベル', light: '×', standard: '×', premium: '◯' },
    { key: 'カスタムブランド', light: '×', standard: '×', premium: '◯' },
    { key: 'サポート', light: 'メール', standard: 'チャット', premium: '電話・優先' },
  ];

  return (
    <TableContainer component={Paper} sx={{ mb: 3 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>機能</TableCell>
            {Object.values(PLANS).map((plan) => (
              <TableCell key={plan.id} align="center">
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {plan.name}
                  </Typography>
                  <Typography variant="h6" color="primary" sx={{ my: 1 }}>
                    ¥{plan.monthlyFee.toLocaleString()}/月
                  </Typography>
                  {plan.id === currentPlanId && (
                    <Chip size="small" label="現在のプラン" color="default" />
                  )}
                  {plan.id === targetPlanId && (
                    <Chip size="small" label="おすすめ" color="primary" />
                  )}
                  {plan.id !== currentPlanId && (
                    <Button
                      size="small"
                      variant={plan.id === targetPlanId ? 'contained' : 'outlined'}
                      color="primary"
                      onClick={() => onSelectPlan(plan.id)}
                      sx={{ mt: 1 }}
                    >
                      選択
                    </Button>
                  )}
                </Box>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {features.map((feature) => (
            <TableRow key={feature.key}>
              <TableCell>{feature.key}</TableCell>
              <TableCell align="center">
                {feature.light === '◯' ? <CheckIcon color="success" /> :
                 feature.light === '×' ? <CancelIcon color="disabled" /> :
                 feature.light}
              </TableCell>
              <TableCell align="center">
                {feature.standard === '◯' ? <CheckIcon color="success" /> :
                 feature.standard === '×' ? <CancelIcon color="disabled" /> :
                 feature.standard}
              </TableCell>
              <TableCell align="center">
                {feature.premium === '◯' ? <CheckIcon color="success" /> :
                 feature.premium === '×' ? <CancelIcon color="disabled" /> :
                 feature.premium}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

/**
 * UpgradePrompt Component
 */
export const UpgradePrompt: React.FC<UpgradePromptProps> = ({
  currentPlan,
  recommendedPlan = 'standard',
  feature,
  open,
  onClose,
  title,
  message,
}) => {
  const [selectedPlan, setSelectedPlan] = useState<string>(recommendedPlan);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(0);

  const currentPlanId = currentPlan?.id || 'light';
  const targetPlan = PLANS[selectedPlan];

  // アクションタイプとテキストを決定
  const actionType = getActionType(currentPlanId, selectedPlan);
  const actionText = getActionText(actionType);

  // デフォルトタイトルを動的に設定
  const displayTitle = title || `プランを${actionText}`;

  // Stripe Checkoutセッションを作成
  const handleUpgrade = async () => {
    setIsProcessing(true);
    setError(null);

    try {
      // Stripeが設定されているか確認
      if (!stripeService.isConfigured()) {
        // 開発環境用のフォールバック
        console.log('Stripe Checkout would be triggered for plan:', selectedPlan);
        // 開発モードでプラン変更を実行
        const store = useStore.getState();
        // 新プラン体系から旧プラン体系へのマッピング
        const mappedPlanId = selectedPlan === 'light' ? 'basic' :
                            selectedPlan === 'premium' ? 'premium' :
                            'standard';
        store.setPlan(mappedPlanId as 'basic' | 'standard' | 'premium' | 'enterprise');
        alert(`プラン「${targetPlan.name}」への${actionText}処理（開発モード）\n\n状態を更新しました。`);
        handleClose();
        return;
      }

      // Stripe Checkoutセッションを作成
      const { url } = await stripeService.createCheckoutSession([
        {
          price: targetPlan.id, // Stripe Price ID
          quantity: 1,
        },
      ]);

      // Stripe Checkoutページへリダイレクト
      window.location.href = url;
    } catch (err) {
      setError(`${actionText}処理中にエラーが発生しました。`);
      console.error('Upgrade error:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  // Stripeカスタマーポータルへリダイレクト
  const handleManageSubscription = async () => {
    setIsProcessing(true);
    try {
      await stripeService.redirectToCustomerPortal();
    } catch (err) {
      setError('カスタマーポータルへの移動に失敗しました。');
      console.error('Customer portal error:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  const steps = ['プラン選択', '内容確認', '支払い'];

  // カスタムクローズハンドラー（状態をリセット）
  const handleClose = () => {
    setActiveStep(0);  // ステップをリセット
    setError(null);    // エラーもクリア
    setSelectedPlan(recommendedPlan); // 選択プランもリセット
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { minHeight: '60vh' } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <UpgradeIcon color="primary" />
            <Typography variant="h6">{displayTitle}</Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {message && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {message}
          </Alert>
        )}

        {feature && (
          <Alert severity="warning" sx={{ mb: 3 }} icon={<StarIcon />}>
            「{feature}」機能を利用するには、{PLANS[recommendedPlan].name}以上への{actionText}が必要です。
          </Alert>
        )}

        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <PlanComparisonTable
            currentPlanId={currentPlanId}
            targetPlanId={selectedPlan}
            onSelectPlan={setSelectedPlan}
          />
        )}

        {activeStep === 1 && (
          <Box>
            <Card sx={{ mb: 3, borderColor: 'primary.main', borderWidth: 2, borderStyle: 'solid' }}>
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  {targetPlan.name}
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  {targetPlan.description}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      月額料金
                    </Typography>
                    <Typography variant="h4" color="primary" gutterBottom>
                      ¥{targetPlan.monthlyFee.toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      年額料金（10%割引）
                    </Typography>
                    <Typography variant="h4" color="secondary" gutterBottom>
                      ¥{targetPlan.yearlyFee?.toLocaleString()}
                    </Typography>
                  </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  含まれる機能
                </Typography>
                <List dense>
                  {targetPlan.features.slice(0, 5).map((f) => (
                    <ListItem key={f.id}>
                      <ListItemIcon>
                        <CheckIcon color="success" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={f.name}
                        secondary={f.description}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Box>
        )}

        {activeStep === 2 && (
          <Box>
            <Card sx={{ mb: 3, textAlign: 'center', p: 3 }}>
              <PaymentIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                安全な決済処理
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                決済処理はStripeを通じて安全に行われます。
                カード情報は暗号化され、安全に保護されます。
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                <Chip icon={<SecurityIcon />} label="SSL暗号化" variant="outlined" />
                <Chip icon={<CheckIcon />} label="PCI準拠" variant="outlined" />
              </Box>
            </Card>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={isProcessing}>
          キャンセル
        </Button>

        {activeStep > 0 && (
          <Button onClick={() => setActiveStep(activeStep - 1)} disabled={isProcessing}>
            戻る
          </Button>
        )}

        {activeStep < steps.length - 1 && (
          <Button
            variant="contained"
            onClick={() => setActiveStep(activeStep + 1)}
            disabled={isProcessing}
          >
            次へ
          </Button>
        )}

        {activeStep === steps.length - 1 && (
          <>
            <Button
              variant="outlined"
              onClick={handleManageSubscription}
              disabled={isProcessing}
              startIcon={isProcessing ? <CircularProgress size={16} /> : <PaymentIcon />}
            >
              既存プランを管理
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpgrade}
              disabled={isProcessing}
              startIcon={isProcessing ? <CircularProgress size={16} /> : <UpgradeIcon />}
            >
              {isProcessing ? '処理中...' : `${targetPlan.name}に${actionText}`}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UpgradePrompt;